import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import IconAction from "@/components/IconAction";
import { Check, X, Lock, Unlock, MailWarning, Send, ChevronDown, ChevronUp, Clock, CheckCheck, TrendingUp } from "lucide-react";
import { useAuth } from "@/lib/auth";
import UserNameLink from "@/components/UserNameLink";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";

const MONTH_FR = ["", "January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

export default function ManagerRequests() {
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();
  const canUnlockCra = hasPermission("unlock_cra");
  // Iter-191i — CEO/ADMIN see the per-submission profitability snapshot inline. Managers
  // intentionally don't (cost data is restricted).
  const canSeeProfitability = user?.role === "CEO" || user?.role === "ADMIN";
  const [subs, setSubs] = useState([]);
  const [unlocks, setUnlocks] = useState([]);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  // Bulk-approve state (iter-32)
  const [selectedSubIds, setSelectedSubIds] = useState([]);
  const [bulkApproving, setBulkApproving] = useState(false);

  // Unsubmitted CRA panel
  const [unsubmittedYear, setUnsubmittedYear] = useState(null);
  const [unsubmittedMonth, setUnsubmittedMonth] = useState(null);
  const [unsubmitted, setUnsubmitted] = useState([]);
  const [unsubOpen, setUnsubOpen] = useState(true);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [reminding, setReminding] = useState(false);

  // Future-month detection — current calendar month/year (UTC-naive client clock).
  const now = new Date();
  const isFutureMonth = (s) => s.year > now.getFullYear() || (s.year === now.getFullYear() && s.month > now.getMonth() + 1);

  const load = async () => {
    const [s, u, missing] = await Promise.all([
      api.get(`/manager/submissions${canSeeProfitability ? "?include_profitability=true" : ""}`),
      api.get("/manager/unlock-requests"),
      api.get("/cra/missions-unsubmitted").catch(() => ({ data: { missions: [], year: null, month: null } })),
    ]);
    setSubs(s.data); setUnlocks(u.data);
    setUnsubmitted(missing.data.missions || []);
    setUnsubmittedYear(missing.data.year);
    setUnsubmittedMonth(missing.data.month);
    setSelectedUserIds([]);
    setSelectedSubIds([]);
  };
  useEffect(() => { load(); }, []);

  const approve = async (id) => {
    try {
      const r = await api.post(`/manager/submissions/${id}/approve`);
      toast.success("CRA approved");
      // Iter-89 Phase 2: when validating the last pending CRA on a sub-contractor mission
      // for the period, the backend returns `trigger_exports` so we can immediately open
      // the Export modal pre-filled. The user can still cancel — the row just stays in
      // "not exported yet" on the CRA Export page.
      const triggers = r?.data?.trigger_exports || [];
      if (triggers.length > 0) {
        const t = triggers[0];
        toast.info(`Tous les CRA de "${t.mission_title}" sont validés — ouverture de l'export…`, { duration: 4000 });
        // Navigate to the CRA Export page with deep-link params so the page auto-opens
        // the send modal for (mission_id, year, month).
        navigate(`/manager/cra-export?auto=${encodeURIComponent(t.mission_id)}&y=${t.year}&m=${t.month}`);
        return;  // skip the reload — user is leaving this page
      }
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const reject = async () => {
    try {
      await api.post(`/manager/submissions/${rejectTarget}/reject`, { reason: rejectReason });
      toast.success("CRA rejected");
      setRejectTarget(null); setRejectReason("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const toggleSubSelect = (id) => setSelectedSubIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  const eligibleSubs = subs.filter((s) => !isFutureMonth(s));
  const allEligibleSelected = eligibleSubs.length > 0 && eligibleSubs.every((s) => selectedSubIds.includes(s.submission_id));
  const toggleAllEligible = () => {
    if (allEligibleSelected) {
      setSelectedSubIds([]);
    } else {
      setSelectedSubIds(eligibleSubs.map((s) => s.submission_id));
    }
  };

  const bulkApprove = async () => {
    if (selectedSubIds.length === 0) return;
    if (!window.confirm(`Approve les ${selectedSubIds.length} CRA sélectionnés ?`)) return;
    setBulkApproving(true);
    try {
      const r = await api.post("/manager/submissions/bulk-approve", { submission_ids: selectedSubIds });
      const { approved_count, error_count, errors = [] } = r.data;
      if (approved_count) toast.success(`${approved_count} CRA approved(s)`);
      if (error_count) toast.error(`${error_count} failure(s) — ${errors[0]?.detail || ""}`);
      setSelectedSubIds([]);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error bulk"); }
    finally { setBulkApproving(false); }
  };
  const approveUnlock = async (id) => {
    await api.post(`/manager/unlock-requests/${id}/approve`);
    toast.success("Unlock approved"); load();
  };
  const rejectUnlock = async (id) => {
    await api.post(`/manager/unlock-requests/${id}/reject`);
    toast.success("Unlock rejected"); load();
  };

  // Unsubmitted helpers
  const allUnsubmittedUserIds = Array.from(new Set(unsubmitted.flatMap((m) => m.unsubmitted_users.map((u) => u.user_id))));

  const toggleUser = (uid) => setSelectedUserIds((p) => p.includes(uid) ? p.filter((x) => x !== uid) : [...p, uid]);
  const selectAll = () => setSelectedUserIds(allUnsubmittedUserIds);
  const clearSelection = () => setSelectedUserIds([]);

  const remindSelected = async () => {
    if (selectedUserIds.length === 0) return toast.error("Select at least one collaborator");
    setReminding(true);
    try {
      const r = await api.post("/cra/bulk-remind", {
        user_ids: selectedUserIds,
        year: unsubmittedYear,
        month: unsubmittedMonth,
      });
      toast.success(`${r.data.reminded}/${r.data.requested} relance(s) sent(s)`);
      clearSelection();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error de relance"); }
    finally { setReminding(false); }
  };

  return (
    <div className="p-4 md:p-8 lg:p-12 max-w-[1400px]">
      <div className="mb-10">
        <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-3 font-mono">Validation</div>
        <h1 className="font-heading text-2xl md:text-4xl font-bold tracking-tight">Pending requests</h1>
      </div>

      {/* ----- CRA non soumis (rappel bulk) ----- */}
      {unsubmitted.length > 0 && (
        <div className="mb-8 bg-amber-50 border border-amber-300 rounded-lg" data-testid="unsubmitted-cra-block">
          <button
            onClick={() => setUnsubOpen((o) => !o)}
            data-testid="unsubmitted-cra-toggle"
            className="w-full flex items-center gap-3 px-5 py-4 text-left"
          >
            <MailWarning size={18} className="text-amber-700 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-heading font-bold text-amber-900">
                CRA non soumis · {unsubmitted.length} mission{unsubmitted.length > 1 ? "s" : ""}
                {unsubmittedMonth && (
                  <span className="ml-2 text-xs font-mono font-normal text-amber-800/80">
                    {MONTH_FR[unsubmittedMonth]} {unsubmittedYear}
                  </span>
                )}
              </div>
              <div className="text-xs text-amber-900/70 mt-0.5">
                {allUnsubmittedUserIds.length} collaborateur{allUnsubmittedUserIds.length > 1 ? "s" : ""} à relancer · sélectionnez et envoyez les rappels en masse.
              </div>
            </div>
            {unsubOpen ? <ChevronUp size={16} className="text-amber-700" /> : <ChevronDown size={16} className="text-amber-700" />}
          </button>

          {unsubOpen && (
            <div className="px-5 pb-5 space-y-3">
              <div className="flex flex-wrap items-center gap-2 px-4 py-2 bg-white/70 border border-amber-200 rounded-md text-xs">
                <button onClick={selectAll} data-testid="unsub-select-all" className="px-2.5 h-7 border border-amber-300 rounded text-amber-900 hover:bg-amber-100">
                  Tout sélectionner ({allUnsubmittedUserIds.length})
                </button>
                <button onClick={clearSelection} data-testid="unsub-clear" className="px-2.5 h-7 text-zinc-600 hover:text-zinc-900">
                  Effacer
                </button>
                <span className="ml-auto font-mono text-amber-900">{selectedUserIds.length} sélectionné(s)</span>
                <button
                  onClick={remindSelected}
                  disabled={reminding || selectedUserIds.length === 0}
                  data-testid="unsub-bulk-remind"
                  className="inline-flex items-center gap-1.5 px-3 h-8 bg-amber-700 text-white rounded hover:bg-amber-800 disabled:opacity-40"
                >
                  <Send size={12} /> {reminding ? "Sending…" : "Send les rappels"}
                </button>
              </div>

              {unsubmitted.map((m) => (
                <div key={m.mission_id} className="bg-white/70 border border-amber-200 rounded-md p-3" data-testid={`unsub-mission-${m.mission_id}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-2 h-2 rounded-full" style={{ background: m.color || "#a16207" }} />
                    <span className="font-medium text-sm text-amber-950">{m.title}</span>
                    <span className="text-[10px] font-mono text-amber-800 bg-amber-100 px-1.5 py-0.5 rounded">{m.unsubmitted_count} en retard</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
                    {m.unsubmitted_users.map((u) => {
                      const checked = selectedUserIds.includes(u.user_id);
                      return (
                        <label
                          key={`${m.mission_id}-${u.user_id}`}
                          className="flex items-center gap-2 px-2 py-1.5 hover:bg-amber-50 rounded cursor-pointer"
                          data-testid={`unsub-user-${m.mission_id}-${u.user_id}`}
                        >
                          <input type="checkbox" checked={checked} onChange={() => toggleUser(u.user_id)} />
                          <div className="min-w-0">
                            <div className="text-sm truncate">
                              <UserNameLink user={u} testid={`unsub-name-link-${m.mission_id}-${u.user_id}`} />
                            </div>
                            <div className="text-[10px] font-mono text-zinc-500 truncate">{u.email}</div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Submissions */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Lock size={16} className="text-zinc-500" />
            <h2 className="font-heading text-lg font-semibold tracking-tight">CRA submissions</h2>
            <span className="ml-auto text-xs font-mono text-zinc-500">{subs.length} pending</span>
          </div>

          {/* Bulk action toolbar */}
          {subs.length > 0 && (
            <div className="bg-white border border-zinc-200 rounded-lg px-4 py-2 mb-3 flex items-center gap-3 text-xs" data-testid="bulk-approve-toolbar">
              <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={allEligibleSelected}
                  onChange={toggleAllEligible}
                  data-testid="bulk-select-all"
                  disabled={eligibleSubs.length === 0}
                />
                Tout sélectionner ({eligibleSubs.length} éligible{eligibleSubs.length > 1 ? "s" : ""})
              </label>
              <span className="ml-auto font-mono text-zinc-600">{selectedSubIds.length} sélectionné(s)</span>
              <button
                onClick={bulkApprove}
                disabled={bulkApproving || selectedSubIds.length === 0}
                data-testid="bulk-approve-btn"
                className="inline-flex items-center gap-1.5 px-3 h-8 bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-40"
              >
                <CheckCheck size={12} /> {bulkApproving ? "En cours…" : "Validate all"}
              </button>
            </div>
          )}

          <div className="bg-white border border-zinc-200 rounded-lg divide-y divide-zinc-100">
            {subs.length === 0 && <div className="px-5 py-12 text-sm text-zinc-500 text-center">Nothing to validate.</div>}
            {subs.map((s) => {
              const future = isFutureMonth(s);
              return (
              <div key={s.submission_id} className="px-5 py-4" data-testid={`sub-row-${s.submission_id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 min-w-0">
                    <input
                      type="checkbox"
                      checked={selectedSubIds.includes(s.submission_id)}
                      onChange={() => toggleSubSelect(s.submission_id)}
                      disabled={future}
                      data-testid={`sub-select-${s.submission_id}`}
                      className="mt-1"
                    />
                    <div className="min-w-0">
                      <div className="font-medium flex items-center gap-2 flex-wrap">
                        <UserNameLink user={{ user_id: s.user_id, name: s.user_name }} testid={`sub-name-link-${s.submission_id}`} />
                        {s.is_manager_submission && (
                          <span data-testid={`sub-manager-badge-${s.submission_id}`} className="inline-flex items-center text-[10px] px-1.5 py-0.5 bg-violet-100 text-violet-700 rounded font-mono uppercase">
                            Manager · CEO only
                          </span>
                        )}
                        {future && (
                          <span data-testid={`sub-future-badge-${s.submission_id}`} className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded font-mono uppercase">
                            <Clock size={10} /> Month à venir
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-zinc-500 font-mono">{s.user_email} · {s.year}-{String(s.month).padStart(2, "0")}</div>
                      {/* Iter-191i — profitability snapshot for CEO/ADMIN: cost/day + mission margin */}
                      {canSeeProfitability && s.profitability && s.profitability.total_days > 0 && (
                        <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px]" data-testid={`sub-profitability-${s.submission_id}`}>
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 border border-blue-200 text-blue-900 rounded font-mono"
                            title={`${s.profitability.total_days} jours imputés × CJM ${Math.round(s.profitability.cjm).toLocaleString("fr-FR")} MAD`}
                            data-testid={`sub-profitability-cost-${s.submission_id}`}
                          >
                            Coût {Math.round(s.profitability.total_cost).toLocaleString("fr-FR")} MAD
                            <span className="text-blue-700/70">({s.profitability.total_days} J × {Math.round(s.profitability.cjm).toLocaleString("fr-FR")})</span>
                          </span>
                          {s.profitability.top_mission_snapshot && (() => {
                            const snap = s.profitability.top_mission_snapshot;
                            const pct = snap.margin_pct;
                            const tone = pct == null ? "bg-zinc-50 border-zinc-200 text-zinc-700"
                              : pct >= 30 ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                              : pct >= 15 ? "bg-amber-50 border-amber-200 text-amber-800"
                              : "bg-red-50 border-red-200 text-red-800";
                            const delta = snap.delta_pp;
                            const showDelta = delta !== null && delta !== undefined && Math.abs(delta) >= 5;
                            const deltaTone = delta <= -5 ? "bg-red-50 border-red-200 text-red-800"
                              : delta >= 5 ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                              : "bg-zinc-50 border-zinc-200 text-zinc-700";
                            return (
                              <>
                                <span
                                  className={`inline-flex items-center gap-1 px-2 py-0.5 border rounded font-mono ${tone}`}
                                  title={`Mission ${snap.mission_title} · CA ${Math.round(snap.revenue_total).toLocaleString("fr-FR")} MAD · Coût ${Math.round(snap.cost_total).toLocaleString("fr-FR")} MAD`}
                                  data-testid={`sub-profitability-mission-${s.submission_id}`}
                                >
                                  <TrendingUp size={10} /> {snap.mission_title} — Marge {pct == null ? "—" : `${pct.toFixed(1)}%`}
                                </span>
                                {/* Iter-191j — drift vs original projection (≥5 pp delta) */}
                                {showDelta && (
                                  <span
                                    className={`inline-flex items-center gap-1 px-2 py-0.5 border rounded font-mono ${deltaTone}`}
                                    title={`Marge projetée à la création : ${snap.projected_margin_pct?.toFixed(1)}%`}
                                    data-testid={`sub-profitability-delta-${s.submission_id}`}
                                  >
                                    {delta > 0 ? `↗ +${delta} pp` : `↘ ${delta} pp`} vs projection
                                  </span>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <IconAction icon={Check} size="lg" tone="success" disabled={future} testid={`approve-${s.submission_id}`} label={future ? "Mois pas encore terminé" : "Approuver le CRA"} onClick={() => approve(s.submission_id)} />
                    <IconAction icon={X} size="lg" tone="danger" disabled={future} testid={`reject-${s.submission_id}`} label={future ? "Mois pas encore terminé" : "Rejeter le CRA"} onClick={() => setRejectTarget(s.submission_id)} />
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        </div>

        {/* Unlocks — Iter-228x-bis gated by `unlock_cra` so a validator
            who has had unlock approval revoked no longer sees this section. */}
        {canUnlockCra && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Unlock size={16} className="text-zinc-500" />
            <h2 className="font-heading text-lg font-semibold tracking-tight">Unlock requests</h2>
            <span className="ml-auto text-xs font-mono text-zinc-500">{unlocks.length} pending</span>
          </div>
          <div className="bg-white border border-zinc-200 rounded-lg divide-y divide-zinc-100">
            {unlocks.length === 0 && <div className="px-5 py-12 text-sm text-zinc-500 text-center">No unlock requests.</div>}
            {unlocks.map((u) => (
              <div key={u.request_id} className="px-5 py-4" data-testid={`unlock-row-${u.request_id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="font-medium">
                      <UserNameLink user={{ user_id: u.user_id, name: u.user_name }} testid={`unlock-name-link-${u.request_id}`} />
                    </div>
                    <div className="text-xs text-zinc-500 font-mono">{u.user_email} · {u.year}-{String(u.month).padStart(2, "0")} · {u.mode}</div>
                    {u.reason && <div className="text-sm text-zinc-700 mt-2">{u.reason}</div>}
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <IconAction icon={Check} size="lg" tone="success" testid={`approve-unlock-${u.request_id}`} label="Approuver la demande de déverrouillage" onClick={() => approveUnlock(u.request_id)} />
                    <IconAction icon={X} size="lg" tone="danger" testid={`reject-unlock-${u.request_id}`} label="Rejeter la demande de déverrouillage" onClick={() => rejectUnlock(u.request_id)} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        )}
      </div>

      <Dialog open={!!rejectTarget} onOpenChange={(o) => !o && setRejectTarget(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reject CRA</DialogTitle></DialogHeader>
          <div className="py-2">
            <Label>Reason</Label>
            <Textarea data-testid="reject-reason" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Explain why…" />
          </div>
          <DialogFooter>
            <button onClick={() => setRejectTarget(null)} className="px-4 h-10 text-sm hover:bg-zinc-100 rounded-md">Cancel</button>
            <button onClick={reject} data-testid="reject-confirm" className="px-4 h-10 bg-zinc-950 text-white text-sm rounded-md">Reject</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
